import type { GazetteerTuple } from "./place-search-core";

export function planMapLabelZooms(minZoom: number, maxZoom: number) {
  const safeMin = Math.max(0, Math.floor(Number.isFinite(minZoom) ? minZoom : 0));
  const safeMax = Math.max(safeMin, Math.floor(Number.isFinite(maxZoom) ? maxZoom : safeMin));
  const candidates = [10, 12, 14, 15, safeMax]
    .map((zoom) => Math.min(safeMax, Math.max(safeMin, zoom)));
  return [...new Set(candidates)].sort((left, right) => left - right);
}

export function mapLabelTupleKey(tuple: GazetteerTuple) {
  const id = String(tuple[0] ?? "").trim();
  if (id) return id;
  const name = String(tuple[1] ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("es");
  const kind = String(tuple[5] ?? "");
  const latitude = Number(tuple[3]);
  const longitude = Number(tuple[4]);
  return `${name}|${kind}|${latitude.toFixed(4)}|${longitude.toFixed(4)}`;
}

function tupleElevation(tuple: GazetteerTuple) {
  const raw: unknown = tuple[8];
  if (raw === null || raw === undefined || raw === "") return null;
  const elevation = Number(raw);
  return Number.isFinite(elevation) ? elevation : null;
}

function tupleWithBestElevation(primary: GazetteerTuple, secondary: GazetteerTuple) {
  const primaryElevation = tupleElevation(primary);
  const secondaryElevation = tupleElevation(secondary);
  if (primaryElevation !== null) return primary;
  if (secondaryElevation === null) return primary;
  const enriched = [...primary] as GazetteerTuple;
  enriched[8] = secondaryElevation;
  enriched[9] = secondary[9] ?? null;
  enriched[10] = secondary[10] ?? null;
  return enriched;
}

export function mergeMapLabelTuples(existing: GazetteerTuple[], incoming: GazetteerTuple[]) {
  const merged = new Map<string, GazetteerTuple>();
  for (const tuple of existing) merged.set(mapLabelTupleKey(tuple), tuple);
  for (const tuple of incoming) {
    const key = mapLabelTupleKey(tuple);
    const current = merged.get(key);
    if (!current) {
      merged.set(key, tuple);
      continue;
    }
    const preferred = Number(tuple[6] ?? 0) > Number(current[6] ?? 0) ? tuple : current;
    const secondary = preferred === tuple ? current : tuple;
    merged.set(key, tupleWithBestElevation(preferred, secondary));
  }
  return [...merged.values()].sort((left, right) => Number(right[6] ?? 0) - Number(left[6] ?? 0));
}

export function mapIndexProgress(
  zooms: number[],
  completedZooms: number[],
  currentZoom: number | null,
  processedTiles: number,
  totalTiles: number,
) {
  if (!zooms.length) return 100;
  const completed = new Set(completedZooms);
  const completedCount = zooms.filter((zoom) => completed.has(zoom)).length;
  let stageFraction = 0;
  if (currentZoom !== null && zooms.includes(currentZoom) && totalTiles > 0) {
    stageFraction = Math.min(1, Math.max(0, processedTiles / totalTiles));
  }
  return Math.min(100, Math.max(0, Math.round(((completedCount + stageFraction) / zooms.length) * 100)));
}
